import { useCallback, useRef } from 'react'
import { isInclusivelyAfterDay, isInclusivelyBeforeDay } from 'react-dates'
import { useTranslation } from 'react-i18next'
import { FiX } from 'react-icons/fi'
import { useDispatch } from 'react-redux'

import moment from 'moment'

import { Button } from '~/components/Buttons'
import DatePicker from '~/components/datepicker'
import { setCompareDates } from '~/components/datepicker-header/datepicker-header-actions'
import { useAppSelector } from '~/store/hooks'

const CompareDatePicker = () => {
  const { t } = useTranslation()

  const { startDate, endDate, compareStartDate, compareEndDate } =
    useAppSelector(state => state.datepickerHeader)

  const dispatch = useDispatch()

  const handleChange = ({ startDate }) => {
    if (startDate) {
      dispatch(setCompareDates({ compareStartDate: startDate }))
    }
  }

  const compareDateKey = useRef(0)
  const handleResetCompare = () => {
    compareDateKey.current += 1
    dispatch(setCompareDates({ compareStartDate: null }))
  }

  const isOutsideRange = useCallback(
    day => {
      if (!startDate || !endDate) return false

      const startDateCompare = moment(startDate).subtract(1, 'days')

      return !isInclusivelyBeforeDay(day, startDateCompare)
    },
    [startDate, endDate]
  )

  return (
    <section className="compare_container d-flex align-items-center">
      <small>{t('rm:chart.compareDatePicker.title')}</small>

      <DatePicker
        key={compareDateKey.current}
        isRangeDate
        defaultStartDate={compareStartDate}
        defaultEndDate={compareEndDate}
        onChange={handleChange}
        hidePresets
        outsideRange={isOutsideRange}
        renderDayContents={(day, modifiers) => {
          const today = moment()
          const referenceStart = startDate
          const referenceEnd = endDate

          const referenceRangeLength =
            referenceStart && referenceEnd
              ? referenceEnd.diff(referenceStart, 'days')
              : 0

          const isInReferenceRange =
            referenceStart &&
            referenceEnd &&
            day.isSameOrAfter(referenceStart, 'day') &&
            day.isSameOrBefore(referenceEnd, 'day')

          const isDayAfterToday = day.isAfter(today, 'day')

          const isAfterEndButTooCloseToToday =
            day.isAfter(endDate, 'day') &&
            referenceRangeLength > 0 &&
            day.diff(today, 'days') < referenceRangeLength

          const isDisabled =
            // isOutsideAllowedRange ||
            isDayAfterToday || isAfterEndButTooCloseToToday

          return (
            <div
              style={{
                backgroundColor: isInReferenceRange
                  ? 'color-mix(in srgb,transparent 75%,var(--sl-color-green-3) 25%)'
                  : undefined,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: isDisabled ? 'none' : undefined
              }}
            >
              {day.date()}
            </div>
          )
        }}
      />

      {compareStartDate && (
        <Button
          text={<FiX />}
          template="criticalTertiary"
          customWidth="auto"
          title={t('rm:remove')}
          onClick={handleResetCompare}
        />
      )}
    </section>
  )
}

export default CompareDatePicker
